'use client';
import React from 'react';
import Typography from '../typography/typography.component';
import { useTranslation } from '@/hook';
import Tooltip from '@mui/material/Tooltip';
import { DisplayedCarInfoInterface } from '@/types/car-details';

enum CarStatusType {
    SOLD = 'sold',
    PURCHASE_IN_PROGRESS = 'purchaseInProgress',
    RESERVED = 'reserved',
    NEWLY_ADDED = 'newlyAdded',
}

interface StatusBadgeProps {
    carData: DisplayedCarInfoInterface;
    showTooltip?: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
    carData,
    showTooltip = true,
}) => {
    const { t } = useTranslation();

    // Method to get the car status type
    const getCarStatusType = (): CarStatusType | null => {
        if (carData.isSold) return CarStatusType.SOLD;
        if (carData.isPurchaseInProgress)
            return CarStatusType.PURCHASE_IN_PROGRESS;
        if (carData.isReserved) return CarStatusType.RESERVED;
        if (carData.isNewlyAdded) return CarStatusType.NEWLY_ADDED;
        return null;
    };

    // Determine status configuration based on car data (same logic as handleBadge)
    const getBadgeConfig = () => {
        const statusType = getCarStatusType();
        if (!statusType) return null;

        switch (statusType) {
            case CarStatusType.SOLD:
                return {
                    backgroundColor: '#CCF5E5',
                    color: '#00673E',
                    text: t('soldPage.soldFlag'),
                    tooltip: null,
                };
            case CarStatusType.PURCHASE_IN_PROGRESS:
                return {
                    backgroundColor: 'bg-secondary-25',
                    textColor: 'text-secondary-700',
                    text: t('purchaseInProgress.soldFlag'),
                    tooltip: t('purchaseInProgress.descriptionLong'),
                };
            case CarStatusType.RESERVED:
                return {
                    backgroundColor: 'bg-gray-100',
                    textColor: 'text-gray-700',
                    text: t('reservedFor24h'),
                    tooltip: t('reservedBadgeTooltipDescription'),
                };
            case CarStatusType.NEWLY_ADDED:
                return {
                    backgroundColor: 'bg-yellow-50',
                    textColor: 'text-warning-700',
                    text: t('Newly Added'),
                    tooltip: null,
                };
            default:
                return null;
        }
    };

    const statusConfig = getBadgeConfig();

    if (!statusConfig) return null;

    const badgeContent = (
        <div className="rounded-md overflow-hidden self-start">
            <Typography
                variant="captionSemiBold"
                className={`px-2 py-1 ${statusConfig.backgroundColor} ${
                    statusConfig.textColor || ''
                }`}
                style={{
                    backgroundColor: statusConfig.backgroundColor.startsWith(
                        '#'
                    )
                        ? statusConfig.backgroundColor
                        : undefined,
                    color: statusConfig.color,
                    paddingLeft: '8px',
                    paddingRight: '8px',
                }}
            >
                {statusConfig.text}
            </Typography>
        </div>
    );

    if (!showTooltip || !statusConfig.tooltip) {
        return badgeContent;
    }

    return (
        <Tooltip
            title={
                <div className="bg-white text-gray-700 border border-gray-200 p-2 rounded-md">
                    <Typography variant="captionMedium">
                        {statusConfig.tooltip}
                    </Typography>
                </div>
            }
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: 'transparent',
                        padding: 0,
                    },
                },
                arrow: {
                    sx: {
                        color: 'white',
                    },
                },
            }}
        >
            {badgeContent}
        </Tooltip>
    );
};

export default StatusBadge;
